import React, {
    FunctionComponent,
    useState,
    ChangeEvent,
    useEffect,
} from 'react'
import { useTranslation } from 'react-i18next'
import some from 'lodash/some'
import { Step3Props } from './types'
import { Step3ValueModel } from '../types'
import { validateStep3, validateRestaurantName } from '../Validation'
import {
    validateMunicipality,
    validateVisitingAddressStreetAddress,
    validateVisitingAddressZipCode,
    validateVisitingAddressCity,
    validatePostalAddressStreetBox,
    validatePostalAddressZipCode,
    validatePostalAddressCity,
} from '../../shared/Validation';
import { FormErrorType } from "@sharedComponents/PantaForm/types";
import { getAllErrors, removeAllErrorsForKey } from "@sharedComponents/PantaForm/utils";
import { InputSelectOption } from "@sharedComponents/InputSelect/types";
import InputText from "@sharedComponents/InputText";
import InputSelect from "@sharedComponents/InputSelect";
import { IMunicipality } from "@apps/Shared/Services/CompanyRegistrations/interfaces";
import InputCheckbox from "@sharedComponents/InputCheckbox";
import InputTextArea from "@sharedComponents/InputTextArea";
import Alert from "@sharedComponents/Alert";
import Button from "@sharedComponents/Button";
import {ButtonType} from '@sharedComponents/Button/types'
import Icon from "@sharedComponents/Icon";

const Step3: FunctionComponent<Step3Props> = props => {
    const { t } = useTranslation()

    const [formErrors, setFormErrors] = useState<FormErrorType[]>([])

    const allErrors = getAllErrors(formErrors)

    const [stepValues, setStepValues] = useState<Step3ValueModel>({
        visitingAddress: {},
        postalAddress: {},
    })

    const rehydrate = (values: Step3ValueModel) => {
        if (values) {
            setStepValues({
                ...stepValues,
                restaurantName: values.restaurantName || '',
                unitName: values.unitName || '',
                municipality: values.municipality,
                visitingAddress: values.visitingAddress || {},
                postalAddress: values.postalAddress || {},
                message: values.message || '',
            } as Step3ValueModel)
        }
    }

    useEffect(() => {
        rehydrate(props.values)
    }, [props.rehydrateFormKey])

    useEffect(() => {
        rehydrate(props.values)
    }, [])

    const onChangeRestaurantName = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setStepValues({ ...stepValues, restaurantName: value })
        props.onChangeValues && props.onChangeValues({ restaurantName: value })
        setFormErrors(validateRestaurantName(value, formErrors))
    }

    const onChangeUnitName = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setStepValues({ ...stepValues, unitName: value })
        props.onChangeValues && props.onChangeValues({ unitName: value })
    }

    const onChangeMunicipality = (option: InputSelectOption) => {
        const value = option
        setStepValues({ ...stepValues, municipality: value })
        props.onChangeValues && props.onChangeValues({ municipality: value })
        setFormErrors(validateMunicipality(value, formErrors))
    }

    const onChangeVisitingAddressStreetAddress = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.value

        setStepValues({
            ...stepValues,
            visitingAddress: {
                ...stepValues.visitingAddress,
                streetAddress: value,
            },
        })

        props.onChangeValues &&
            props.onChangeValues({
                visitingAddress: {
                    ...stepValues.visitingAddress,
                    streetAddress: value,
                },
            })

        setFormErrors(validateVisitingAddressStreetAddress(value, formErrors))
    }

    const onChangeVisitingAddressZipCode = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        let value = e.target.value
        if (value.length > 4) {
            value = value.trimEnd()
        }

        const visitingAddress = {
            ...stepValues.visitingAddress,
            zipCode: value,
        }

        setStepValues({
            ...stepValues,
            visitingAddress,
        })

        props.onChangeValues && props.onChangeValues({ visitingAddress })
        setFormErrors(validateVisitingAddressZipCode(value, true, formErrors))
    }

    const onChangeVisitingAddressCity = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        const visitingAddress = {
            ...stepValues.visitingAddress,
            city: value,
        }

        setStepValues({
            ...stepValues,
            visitingAddress,
        })

        props.onChangeValues && props.onChangeValues({ visitingAddress })
        setFormErrors(validateVisitingAddressCity(value, formErrors))
    }

    const onChangeVisitingAddressIsAlsoPostalAddress = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.checked

        const visitingAddress = {
            ...stepValues.visitingAddress,
            isAlsoPostalAddress: value,
        }

        const postalAddress = value === true ? {} : stepValues.postalAddress

        setStepValues({
            ...stepValues,
            visitingAddress,
            postalAddress,
        })

        props.onChangeValues &&
            props.onChangeValues({ visitingAddress, postalAddress })

        if (value === true) {
            let errors = removeAllErrorsForKey(
                formErrors,
                'postalAddress.streetBox'
            )
            errors = removeAllErrorsForKey(errors, 'postalAddress.zipCode')
            errors = removeAllErrorsForKey(errors, 'postalAddress.city')

            setFormErrors(errors)
        }
    }

    const onChangePostalAddressStreetBox = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.value

        const postalAddress = {
            ...stepValues.postalAddress,
            streetBox: value,
        }

        setStepValues({ ...stepValues, postalAddress })
        props.onChangeValues && props.onChangeValues({ postalAddress })
        setFormErrors(
            validatePostalAddressStreetBox(
                value,
                !!stepValues.visitingAddress.isAlsoPostalAddress,
                formErrors
            )
        )
    }

    const onChangePostalAddressZipCode = (e: ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value
        if (value.length > 4) {
            value = value.trimEnd()
        }

        const postalAddress = {
            ...stepValues.postalAddress,
            zipCode: value,
        }

        setStepValues({ ...stepValues, postalAddress })
        props.onChangeValues && props.onChangeValues({ postalAddress })
        setFormErrors(
            validatePostalAddressZipCode(
                value,
                !!stepValues.visitingAddress.isAlsoPostalAddress,
                true,
                formErrors
            )
        )
    }

    const onChangePostalAddressCity = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        const postalAddress = {
            ...stepValues.postalAddress,
            city: value,
        }

        setStepValues({ ...stepValues, postalAddress })
        props.onChangeValues && props.onChangeValues({ postalAddress })
        setFormErrors(
            validatePostalAddressCity(
                value,
                !!stepValues.visitingAddress.isAlsoPostalAddress,
                formErrors
            )
        )
    }

    const onChangeMessage = (e: ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value

        setStepValues({
            ...stepValues,
            message: value,
        })

        props.onChangeValues && props.onChangeValues({ message: value })
    }

    const nextStep = () => {
        validateStep3(stepValues, formErrors).then(
            (errors: FormErrorType[]) => {
                if (errors && errors.length > 0) {
                    setFormErrors(errors)
                } else {
                    setFormErrors([])
                    props.onChangeValues && props.onChangeValues({})
                    props.nextStep && props.nextStep()
                }
            }
        )
    }

    const prevStep = () => {
        props.prevStep && props.prevStep()
    }

    const onCancel = () => {
        setFormErrors([])

        props.onCancel && props.onCancel()
    }

    const uniqueErrors = (key: string) => {
        const errors = formErrors.find(err => err.key === key);
        const uniqueErr = errors ? [...new Set(errors.messages)] : [];

        return uniqueErr.length > 0 ? uniqueErr.join(', ') : '';
    }

    return (
        <div className='form-step-wrapper form-step-wrapper--wide'>
            <h3>
                {t('companyRegistration.customer.restaurant.stepLabel.step3')}
            </h3>
            <p className="required-asterisk mt-3">{t('global.requiredAsterisk')}</p>

            {props.cmsContent ? (
                <div className='form-step-wrapper__content'>
                    {props.cmsContent}
                </div>
            ) : (
                ''
            )}
            <div className='fields-wrapper'>
                <div className='fields-wrapper__field'>
                    <InputText
                        id="restaurant-name"
                        label={t(
                            'companyRegistration.customer.restaurant.restaurantName'
                        )}
                        value={stepValues.restaurantName || ''}
                        hasError={!!some(formErrors, ['key', 'restaurantName'])}
                        required={true}
                        maxLength={50}
                        onChange={onChangeRestaurantName}
                        errorText={uniqueErrors('restaurantName')}
                    />
                </div>
                <div className='fields-wrapper__field'>
                    <InputText
                        id="unit-name"
                        label={t('companyRegistration.customer.unitName')}
                        value={stepValues.unitName || ''}
                        maxLength={50}
                        hasError={!!some(formErrors, ['key', 'unitName'])}
                        onChange={onChangeUnitName}
                    />
                </div>
                <div className='fields-wrapper__field'>
                    <InputSelect
                        id="municipality"
                        emptyMessage={t('global.choose')}
                        hideLabel={false}
                        label={t('companyRegistration.customer.municipality')}
                        value={stepValues.municipality}
                        onChange={onChangeMunicipality}
                        hasError={!!some(formErrors, ['key', 'municipality'])}
                        options={props.municipalities.map(
                            (municipality: IMunicipality) => {
                                return {
                                    label: municipality.description,
                                    value: municipality.code + '',
                                } as InputSelectOption
                            }
                        )}
                        required={true}
                        disabled={props.municipalities.length === 0}
                        errorText={uniqueErrors('municipality')}
                    />
                </div>
                {
                    <>
                        <h5 className='fields-wrapper__title'>
                            {t('companyRegistration.customer.visitingAddress')}
                        </h5>
                        <div className='fields-wrapper__field'>
                            <InputText
                                id="visiting-address-street-address"
                                label={t('companyRegistration.streetAddress')}
                                value={
                                    stepValues.visitingAddress.streetAddress ||
                                    ''
                                }
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'visitingAddress.streetAddress',
                                    ])
                                }
                                maxLength={100}
                                onChange={onChangeVisitingAddressStreetAddress}
                                required={true}
                                errorText={uniqueErrors('visitingAddress.streetAddress')}
                            />
                        </div>
                        <div className='fields-wrapper__field'>
                            <InputText
                                id="visiting-address-zip-code"
                                label={t('companyRegistration.zipCode')}
                                value={stepValues.visitingAddress.zipCode || ''}
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'visitingAddress.zipCode',
                                    ])
                                }
                                onChange={onChangeVisitingAddressZipCode}
                                required={true}
                                errorText={uniqueErrors('visitingAddress.zipCode')}
                            />
                        </div>
                        <div className='fields-wrapper__field'>
                            <InputText
                                id="visiting-address-city"
                                label={t('companyRegistration.city')}
                                value={stepValues.visitingAddress.city || ''}
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'visitingAddress.city',
                                    ])
                                }
                                maxLength={50}
                                onChange={onChangeVisitingAddressCity}
                                required={true}
                                errorText={uniqueErrors('visitingAddress.city')}
                            />
                        </div>
                        <div className='fields-wrapper__field'>
                            <InputCheckbox
                                id="visiting-address-is-also-postal-address"
                                label={t(
                                    'companyRegistration.customer.visitingAddressIsAlsoPostalAddress'
                                )}
                                onChange={
                                    onChangeVisitingAddressIsAlsoPostalAddress
                                }
                                checked={
                                    stepValues.visitingAddress
                                        .isAlsoPostalAddress || false
                                }
                            />
                        </div>
                    </>
                }
                {
                    <>
                        <h5 className={`fields-wrapper__title ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__title--disabled' : ''}`}>
                            {t('companyRegistration.customer.postalAddress')}
                        </h5>
                        <div className={`fields-wrapper__field ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__field--disabled' : ''}`}>
                            <InputText
                                id="postal-address-street-box"
                                label={t(
                                    'companyRegistration.customer.streetBox'
                                )}
                                value={stepValues.postalAddress.streetBox || ''}
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'postalAddress.streetBox',
                                    ])
                                }
                                maxLength={100}
                                onChange={onChangePostalAddressStreetBox}
                                required={true}
                                disabled={
                                    stepValues.visitingAddress
                                        .isAlsoPostalAddress
                                }
                                errorText={uniqueErrors('postalAddress.streetBox')}
                            />
                        </div>
                        <div className={`fields-wrapper__field ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__field--disabled' : ''}`}>
                            <InputText
                                id="postal-address-zip-code"
                                label={t('companyRegistration.zipCode')}
                                value={stepValues.postalAddress.zipCode || ''}
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'postalAddress.zipCode',
                                    ])
                                }
                                onChange={onChangePostalAddressZipCode}
                                required={true}
                                disabled={
                                    stepValues.visitingAddress
                                        .isAlsoPostalAddress
                                }
                                errorText={uniqueErrors('postalAddress.zipCode')}
                            />
                        </div>
                        <div className={`fields-wrapper__field ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__field--disabled' : ''}`}>
                            <InputText
                                id="postal-address-city"
                                label={t('companyRegistration.city')}
                                value={stepValues.postalAddress.city || ''}
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'postalAddress.city',
                                    ])
                                }
                                onChange={onChangePostalAddressCity}
                                required={true}
                                maxLength={50}
                                disabled={
                                    stepValues.visitingAddress
                                        .isAlsoPostalAddress
                                }
                                errorText={uniqueErrors('postalAddress.city')}
                            />
                        </div>
                    </>
                }
                {
                    <>
                        <h5 className='fields-wrapper__title'>
                            {t('companyRegistration.customer.messageForRp')}
                        </h5>
                        <div className='fields-wrapper__field'>
                            <InputTextArea
                                id="message-to-rp"
                                label={t(
                                    'companyRegistration.customer.message'
                                )}
                                value={stepValues.message || ''}
                                hasError={
                                    !!some(formErrors, ['key', 'message'])
                                }
                                maxLength={512}
                                onChange={onChangeMessage}
                            />
                        </div>
                    </>
                }
            </div>
            {allErrors.length > 0 && (
                <div className='form-step-wrapper__error'>
                    <Alert
                        type="danger"
                        text={t(
                            'companyRegistration.companyInfoContainsErrors'
                        )}
                        subtext={allErrors}
                        compact={false}
                    />
                </div>
            )}
            {props.unexpectedErrors.length > 0 && (
                <Alert
                    type="danger"
                    text={''}
                    subtext={props.unexpectedErrors}
                    compact={true}
                />
            )}
            <div className='buttons-wrapper'>
                <Button
                    type={ButtonType.PRIMARY}
                    className="next-button"
                    text={
                        <span className='buttons-wrapper__button-text buttons-wrapper__button-text--next'>
                            <span>{t('button.next')}</span>
                            <Icon iconName="arrow-right" />
                        </span>
                    }
                    onClick={nextStep}
                />
                <Button
                    type={ButtonType.PRIMARY}
                    className="prev-button"
                    text={
                        <span className='buttons-wrapper__button-text buttons-wrapper__button-text--prev'>
                            <Icon iconName="arrow-left" />
                            <span>{t('button.previous')}</span>
                        </span>
                    }
                    onClick={prevStep}
                />
                <Button
                    type={ButtonType.SECONDARY}
                    className="cancel-button"
                    text={
                        <span className='buttons-wrapper__button-text'>
                            <Icon iconName="close" />
                            <span>{t('button.cancel')}</span>
                        </span>
                    }
                    onClick={onCancel}
                />
            </div>
        </div>
    )
}

export default Step3